// Top-3 RxNorm candidate confirmation, plus optional "Set reminders
// from the label" step when the OCR pipeline extracted dosing
// instructions. On confirm:
//   1. POST /api/medications with the chosen RxCUI + canonical name
//      + frequency (defaulted to the OCR's dosing text when present).
//   2. If the user opted in to auto-reminders, POST /api/reminders
//      once per parsed slot using the user's wake/bed prefs to pick
//      sensible default times.
// Playbook reference: Section 5.10.

import { BlockStack, Page, Text } from '@shopify/polaris';
import { useState } from 'react';
import type { OcrCandidate, OcrDosingHint } from '../api/ocr';
import { useAuthedFetch } from '../hooks/useAuthedFetch';
import { useMe } from '../hooks/useMe';
import { AmazonReorderLink, type ProductKind } from './AmazonReorderLink';
import { SeniorBanner } from './senior/SeniorBanner';
import { SeniorButton } from './senior/SeniorButton';
import { SeniorCard } from './senior/SeniorCard';

interface Props {
  candidates: OcrCandidate[];
  dosingHint?: OcrDosingHint | null;
  confidence?: 'high' | 'medium' | 'low';
  onCancel: () => void;
  onSelected: () => void;
}

const SLOT_LABEL: Record<string, string> = {
  morning: 'Morning',
  noon: 'Noon',
  evening: 'Evening',
  bedtime: 'Bedtime',
  asneeded: 'As needed',
};

function slotToTime(slot: string, wakeTime: string, bedTime: string): string {
  const toMin = (s: string) => {
    const [h, m] = s.split(':').map(Number);
    return ((h ?? 0) * 60 + (m ?? 0)) % 1440;
  };
  const toStr = (m: number) =>
    `${Math.floor(m / 60)
      .toString()
      .padStart(2, '0')}:${(m % 60).toString().padStart(2, '0')}`;
  const wake = toMin(wakeTime);
  const bed = toMin(bedTime);
  const span = bed > wake ? bed - wake : 1440 - wake + bed;
  switch (slot) {
    case 'morning':
      return toStr(wake);
    case 'noon':
      return toStr((wake + Math.round(span * 0.33)) % 1440);
    case 'evening':
      return toStr((wake + Math.round(span * 0.66)) % 1440);
    case 'bedtime':
      return toStr(bed);
    default:
      return '09:00';
  }
}

export function CandidatePicker({
  candidates,
  dosingHint,
  confidence,
  onCancel,
  onSelected,
}: Props) {
  const authedFetch = useAuthedFetch();
  const { me } = useMe();
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [setReminders, setSetReminders] = useState(true);

  const hasRealSlots = Boolean(
    dosingHint &&
      Array.isArray(dosingHint.slots) &&
      dosingHint.slots.length > 0 &&
      !dosingHint.asNeeded,
  );

  async function pick(c: OcrCandidate) {
    setSubmitting(c.rxcui);
    setError(null);
    try {
      const frequency = dosingHint?.text ?? 'as prescribed';
      // Synthetic candidates come from the Gemini/OpenFDA fallback
      // when RxNorm failed to match; they carry a sentinel rxcui
      // like "synthetic-name-foo". Don't send that to the API, just
      // post the plain name and let the server store rxcui as null.
      const isSynthetic = c.rxcui.startsWith('synthetic-');
      const res = await authedFetch('/api/medications', {
        method: 'POST',
        body: JSON.stringify({
          name: c.displayName,
          frequency,
          rxcui: isSynthetic ? undefined : c.rxcui,
          strength: dosingHint?.strength ?? undefined,
          addedVia: 'PHOTO_OCR',
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: { message?: string } };
        throw new Error(body.error?.message ?? `HTTP ${res.status}`);
      }
      const created = (await res.json()) as { ok: boolean; data?: { id?: string } };
      const medicationId = created.data?.id;

      // Auto-create reminders from the parsed slots, using the user's
      // wake/bed prefs to pick sensible times.
      if (
        medicationId &&
        setReminders &&
        hasRealSlots &&
        dosingHint &&
        Array.isArray(dosingHint.slots)
      ) {
        const wakeTime = me?.wakeTime ?? '08:00';
        const bedTime = me?.bedTime ?? '22:00';
        await Promise.all(
          dosingHint.slots
            .filter((s) => s !== 'asneeded')
            .map((slot) =>
              authedFetch('/api/reminders', {
                method: 'POST',
                body: JSON.stringify({
                  medicationId,
                  fireAt: slotToTime(slot, wakeTime, bedTime),
                  slot,
                  channel: 'email',
                }),
              }).catch(() => null),
            ),
        );
      }

      onSelected();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(null);
    }
  }

  // High-confidence ensemble agreement → drop the "pick the right one"
  // framing. Low-confidence → flag uncertainty so the senior compares.
  const title =
    confidence === 'high' && candidates.length === 1
      ? 'Confirm this is your medicine'
      : confidence === 'low'
        ? 'We were not sure. Please pick the closest match'
        : 'Which medicine is this?';

  return (
    <Page title={title}>
      <BlockStack gap="400">
        <SeniorCard body="Pick the option that matches the label on your bottle. If none of these look right, go back and try the photo again or type the name.">
          <BlockStack gap="300">
            {confidence === 'high' ? (
              <SeniorBanner title="Match looks strong" tone="success">
                Two readings of your label agreed on this medicine.
              </SeniorBanner>
            ) : confidence === 'low' ? (
              <SeniorBanner title="Lower confidence" tone="warning">
                The photo was harder to read than usual. Double-check the name on your bottle.
              </SeniorBanner>
            ) : null}
            {error ? (
              <SeniorBanner title="Could not add this medication" tone="critical">
                {error}
              </SeniorBanner>
            ) : null}

            {dosingHint?.text ? (
              <div
                style={{
                  background: 'var(--color-accent-soft)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-md)',
                  fontSize: 15,
                  color: 'var(--color-text-primary)',
                  lineHeight: 1.4,
                }}
              >
                <strong>Siggy read this off the label:</strong> {dosingHint.text}
                {dosingHint.strength ? <> ({dosingHint.strength})</> : null}
              </div>
            ) : null}

            {candidates.map((c) => (
              <SeniorButton
                key={c.rxcui}
                onClick={() => void pick(c)}
                fullWidth
                size="large"
                disabled={submitting !== null}
                data-testid={`candidate-${c.rxcui}`}
              >
                {c.displayName}
              </SeniorButton>
            ))}

            {/* Amazon affiliate reorder prompt, only for OTC + supplements
                (gated inside the component by the Gemini-extracted kind
                or a name-based heuristic). Shown above the auto-reminder
                toggle so the user can copy the link before submitting. */}
            {candidates.length > 0 ? (
              <AmazonReorderLink
                name={candidates[0]?.displayName ?? ''}
                kind={(dosingHint?.kind as ProductKind | undefined) ?? undefined}
                variant="card"
              />
            ) : null}

            {hasRealSlots && dosingHint ? (
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 12px',
                  border: '1px solid var(--color-border-subtle)',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--color-surface-secondary)',
                }}
              >
                <input
                  type="checkbox"
                  checked={setReminders}
                  onChange={(e) => setSetReminders(e.target.checked)}
                  style={{ width: 20, height: 20 }}
                />
                <span style={{ fontSize: 15, lineHeight: 1.4 }}>
                  Also set email reminders (
                  {dosingHint.slots
                    .filter((s) => s !== 'asneeded')
                    .map((s) => SLOT_LABEL[s])
                    .join(', ')}
                  ) using my wake / bedtime in Settings
                </span>
              </label>
            ) : null}

            <Text as="p" variant="bodySm" tone="subdued">
              {candidates.length} option{candidates.length === 1 ? '' : 's'} found.
            </Text>
            <SeniorButton onClick={onCancel} fullWidth>
              None of these, go back
            </SeniorButton>
          </BlockStack>
        </SeniorCard>
      </BlockStack>
    </Page>
  );
}
